import * as Mui from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";

import WarningIcon from "@mui/icons-material/Warning";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { ContextOperation } from "../../context/OperationContext";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import OperationTypes from "../../types";
import AutoComplete, { AutoCompleteOption } from "../common/AutoComplete";
import CheckInOutWrapper from "../common/CheckInOutWrapper";
import VehicleEntranceService from "./service/VehicleEntranceService";
import { formatTypeString } from "./utils";
import { AxiosError } from "axios";

export interface FormInputs {
  position: string | null;
}

export interface Driver {
  label: string;
  value: string;
}

type VehicleEntranceInternalDDT = {
  position: string,
  formDamageIds: number[],
} & OperationTypes.Vehicle;

const VehicleEntranceInternalDDT = (props: any) => {
  const { setLoading, userLocations } = useContext(ContextOperation);

  const navigate = useNavigate();
  const location = useLocation();
  const t = useOperationTranslation();

  props.changeSubtitle(t("vehicleEntrance.subtitles.internal"));

  const { i18n } = useTranslation();
  const snackbar = useSnackbar();
  const form = useForm<any>({
    defaultValues: {
      position: null,
    },
  });

  const [check, setCheck] = useState(false);
  const [allLocations, setAllLocations] = useState<any>([]);
  const {globalData} = useContext(GlobalContext);
  const [username, setUsername] = useState("");
  const [vehicles, setVehicles] = useState<(VehicleEntranceInternalDDT)[]>([]);

  const [warningDialog, setWarningDialog] = useState(false);

  const vehicleData: any = location.state;

  const [selectedVehicle, setSelectedVehicle] = useState<OperationTypes.Vehicle | null>(null);
  const [vehiclesCheckedOut, setVehiclesCheckedOut] = useState<(string | undefined)[]>([]);

  const onOpenCheckOut = (vehicle: OperationTypes.Vehicle) => {
    setSelectedVehicle(vehicle);
  }

  const onCloseCheckOut = (ret: OperationTypes.CheckInOutType.retObject) => {
    setVehiclesCheckedOut([
      ...vehiclesCheckedOut,
      selectedVehicle?.plate,
    ]);

    const newVehicles = vehicles.map((vehicle) => {
      if (vehicle.plate === selectedVehicle?.plate) {
          const newVehicle = {
              ...vehicle,
              formDamageIds: [
                  ...ret.eventsExternal.filter(event => event.id !== null && event.id !== undefined).map(event => event.id),
                  ...ret.eventsInternal.filter(event => event.id !== null && event.id !== undefined).map(event => event.id),
              ]
          };

          if (ret.equipmentsEvent.id !== null && ret.equipmentsEvent.id !== undefined) {
            newVehicle.formDamageIds.push(ret.equipmentsEvent.id);
          }
          return newVehicle;
      }
      return vehicle;
    });
    setVehicles(newVehicles);

    // TODO fare un controllo per capire se il checkin è andato a buon fine
    // Attualmente eseguo solo un controllo per verificare che esista la targa
    // per poter mostrare la snackbar di successo checkin.
    // In futuro va fatto un controllo più approfondito.
    if (selectedVehicle?.plate) {
      snackbar.show(
        "success",
        t("vehicleEntrance.withPlate.checkin.success").replace(
          "#plate",
          selectedVehicle?.plate
        )
      );
    }
    
    setSelectedVehicle(null);
  }

  useEffect(() => {
    if (vehicleData) {
      setVehicles(vehicleData.vehicles);
    }
  }, [vehicleData]);
  
  useEffect(() => {
    if (vehicleData) {
      setAllLocations(
        vehicleData?.allLocations.map((item: any) => {
          return {
            ...item,
            label: `${item.locationEntityDesc} (${formatTypeString(
              item.type,
              t
            )})`,
            value: item.locationEntityDesc,
          };
        })
      );
    }

    if (userLocations.length > 0) {
      let associatedUser = false;
      userLocations.map((loc: any) => {
        if (loc.bpCode === (location.state as any).locationEnd.bpCode) {
          associatedUser = true;
        }
      });
      if (!associatedUser) {
        setWarningDialog(true);
      }
    }
    
    setUsername(globalData.user.username);

  }, [i18n?.language, userLocations]);

  const onSubmit = (data: any) => {
    setLoading(true);
    VehicleEntranceService.add
      .confirmEntranceInternal(vehicleData.tenant, {
        deliveryNoteNumber: vehicleData.deliveryNoteNumber,
        actualEndStationId: userLocations.filter(
          (location: any) => location.key === data.position
        )[0]?.bpCode,
        actualEndStationType: userLocations.filter(
          (location: any) => location.key === data.position
        )[0]?.typeEnum,
        vehicles: vehicles.map((vehicle: VehicleEntranceInternalDDT, index: any) => {
          return {
            plate: vehicle.plate,
            position: data[`position${index}`],
            formDamageIds: vehicle.formDamageIds,
          };
        }),
      })
      .then(
        () => {
          setLoading(false);
          snackbar.show(
            "success",
            t("vehicleEntrance.withPlate.confirm.success").replace(
              "#plateNotFound",
              vehicleData.deliveryNoteNumber
            )
          );
          navigate(`/operation/transfer/vehiclesEntrance`);
        },
        (error: AxiosError<OperationTypes.AxiosErrorResponse>) => {
          setLoading(false);

          if (error.response?.status === 400 && error.response.data.subCode === 11) {
            snackbar.show("error", t("vehicleEntrance.internalDDT.400ErrorSubCode11"));
          }
          else if (error.response?.status === 400) {
            snackbar.show("error", t("vehicleEntrance.internalDDT.400Error"));
          }
          else if (error.response?.status === 404) {
            snackbar.show("error", t("vehicleEntrance.internalDDT.404Error").replace("#plateNotFound", vehicleData.deliveryNoteNumber));
          }
          if (error.response?.status === 409) {
            snackbar.show("error", t("vehicleEntrance.internalDDT.409Error").replace("#plateNotFound", vehicleData.deliveryNoteNumber));
          }
          else {
            snackbar.show("error", t("vehicleEntrance.internalDDT.500Error"));
          }
        }
      );
  };

  const locations = useMemo<AutoCompleteOption[]>(() => {
    return userLocations.map((loc) => ({
      label: loc.descr || "",
      value: loc.key || "",
    })) || [];
  }, [userLocations]);

  return (
    <Mui.Box padding={{ md: 2 }}>
      <Mui.Paper>
        <Mui.Typography
          variant="h4"
          sx={{
            px: 2,
            py: 2,
            textAlign: "center",
            whiteSpace: "pre-line",
          }}
        >
          {t("vehicleEntrance.withPlate.confirm.headerTitle")}
        </Mui.Typography>
        <Mui.Box paddingBottom="16px">
          {!selectedVehicle && (
            <Mui.Grid container spacing={{ md: 2 }} p={{ md: 2 }}>
              <Mui.Grid item md={6} xs={12}>
                <Mui.Card>
                  <Mui.CardContent>
                    <Mui.Box
                      sx={{ display: "flex", gap: "10px", alignItems: "center" }}
                      pt="20px"
                    >
                      <Mui.Grid container spacing={2}>
                        <Mui.Grid
                          item
                          xs={12}
                          sx={{ display: "flex", flexDirection: "row" }}
                        >
                          <Mui.Typography
                            variant="body1"
                            sx={{ minWidth: "40px" }}
                          >
                            {t(
                              "vehicleEntrance.withPlate.confirm.referenceTitle"
                            )}
                            :
                          </Mui.Typography>
                          <Mui.Typography variant="h5" sx={{ px: 2 }}>
                            {vehicleData.ddt}
                          </Mui.Typography>
                        </Mui.Grid>
                        <Mui.Grid
                          item
                          xs={12}
                          md={6}
                          sx={{ display: "flex", flexDirection: "row" }}
                        >
                          <Mui.Typography
                            variant="body1"
                            sx={{ minWidth: "40px" }}
                          >
                            {t("vehicleEntrance.withPlate.confirm.from")}
                          </Mui.Typography>
                          <Mui.Typography variant="h5" sx={{ px: 2 }}>
                            {`${
                              vehicleData.locationStart.value
                            } (${formatTypeString(
                              vehicleData.locationStart.type,
                              t
                            )})`}
                          </Mui.Typography>
                        </Mui.Grid>
                        <Mui.Grid
                          item
                          xs={12}
                          md={6}
                          sx={{ display: "flex", flexDirection: "row" }}
                        >
                          <Mui.Typography
                            variant="body1"
                            sx={{ minWidth: "40px" }}
                          >
                            {t("vehicleEntrance.withPlate.confirm.to")}
                          </Mui.Typography>
                          <Mui.Typography variant="h5" sx={{ px: 2 }}>
                            {`${
                              vehicleData.locationEnd.value
                            } (${formatTypeString(
                              vehicleData.locationEnd.type,
                              t
                            )})`}
                          </Mui.Typography>
                        </Mui.Grid>
                      </Mui.Grid>
                    </Mui.Box>

                    <Mui.Box>
                      <Mui.Typography
                        variant="body1"
                        sx={{ minWidth: "40px" }}
                        pt="20px"
                      >
                        {t("vehicleEntrance.confirm.location")}:
                      </Mui.Typography>

                      <AutoComplete
                        fullWidth
                        options={locations}
                        placeholder={t(
                          "vehicleEntrance.confirm.locationPlaceholder"
                        )}
                        {...form.register("position", {
                          required: t("vehicleEntrance.status.lmsErrorMessage"),
                        })}
                        value={form.watch("position") || ""}
                        onChange={(newStatusValue) => {
                          form.setValue("position", newStatusValue as string);
                        }}
                        error={form.formState.errors?.position?.message?.toString()}
                      />
                    </Mui.Box>
                  </Mui.CardContent>
                </Mui.Card>
              </Mui.Grid>

              <Mui.Grid item md={6} xs={12}>
                <Mui.Card>
                  <Mui.CardContent sx={{ paddingLeft: 3, paddingRight: 3 }}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                      <>
                        {vehicles.map((vehicle: OperationTypes.Vehicle, index: any) => {
                          return (
                            <Mui.Box key={index}>
                              <Mui.Grid container spacing={2} alignItems="center">
                                <Mui.Grid item xs={3}>
                                  <Mui.Typography variant="body">
                                    {vehicle.plate}
                                  </Mui.Typography>
                                </Mui.Grid>

                                <Mui.Grid item xs={3}>
                                  <Mui.Typography variant="body">
                                    {vehicle.model.brand.descr +
                                      " " +
                                      vehicle.model.descr}
                                  </Mui.Typography>
                                </Mui.Grid>
                                
                                <Mui.Grid item xs={3}>
                                  <Mui.TextField
                                    variant="standard"
                                    fullWidth
                                    placeholder={t("vehicleEntrance.status.positionLabel")}
                                    {...form.register(`position${index}`)}
                                    value={form.watch(`position${index}`)}
                                    onChange={(event) => {
                                      form.setValue(
                                        `position${index}`,
                                        event.target.value
                                      );
                                    }}
                                    helperText={form.formState.errors[
                                      `position${index}`
                                    ]?.message?.toString()}
                                    error={Boolean(
                                      form.formState.errors[`position${index}`]
                                    )}
                                  />
                                </Mui.Grid>

                                <Mui.Grid item xs={3}>
                                  <Mui.Button
                                    onClick={() => onOpenCheckOut(vehicle)}
                                    disabled={vehiclesCheckedOut.includes(vehicle.plate)}
                                  >
                                    {t("vehicleEntrance.status.checkInButton")}
                                  </Mui.Button>
                                </Mui.Grid>
                              </Mui.Grid>
                            </Mui.Box>
                          );
                        })}

                        <Mui.Grid item xs={12} mt={2}>
                          <Mui.Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Mui.Typography variant="body">
                              {t("vehicleEntrance.withPlate.confirm.checkboxTitle")}
                            </Mui.Typography>

                            <Mui.Checkbox
                              checked={check}
                              onClick={() => setCheck(!check)}
                              color={"success"}
                              size="medium"
                            />
                          </Mui.Box>

                          <Mui.Button
                            disabled={!check || vehiclesCheckedOut.length !== vehicles.length}
                            fullWidth
                            variant="contained"
                            type="submit"
                          >
                            {t("vehicleEntrance.withPlate.confirm.buttonLabel")}
                          </Mui.Button>
                        </Mui.Grid>
                      </>

                      <Mui.Dialog open={warningDialog}>
                        <Mui.DialogTitle>
                          <WarningIcon
                            sx={{ color: "orange", fontSize: "20px" }}
                          />
                        </Mui.DialogTitle>

                        <Mui.DialogContent>
                          {t(
                            "vehicleExit.status.fromLocationNotAssociated"
                          ).replace("username", username)}
                        </Mui.DialogContent>

                        <Mui.DialogActions>
                          <Mui.Button
                            variant="contained"
                            onClick={() => setWarningDialog(false)}
                          >
                            OK
                          </Mui.Button>
                        </Mui.DialogActions>
                      </Mui.Dialog>

                    </form>
                  </Mui.CardContent>
                </Mui.Card>
              </Mui.Grid>
            </Mui.Grid>
          )}

          {selectedVehicle && (
            <CheckInOutWrapper
              brandDescription={selectedVehicle.model.brand.descr}
              callbackButtonLabel={t("vehicleEntrance.status.checkInCBButton")}
              currentBatteryPercentage={selectedVehicle.currentElectricTankFilling}
              currentFuelLevelEighths={selectedVehicle.currentTermicTankFilling}
              currentKms={selectedVehicle.kms}
              enginePowerGroupCode={selectedVehicle.enginePowerCode}
              modelDescription={selectedVehicle.model.descr}
              oprCallback={onCloseCheckOut}
              plate={selectedVehicle.plate}
              carClass={selectedVehicle.carClass.descr}
              bypassDamagesEquipments={false}
			  enableCounterPart={true}
			  viewType='ddt'
            />
          )}
        </Mui.Box>
      </Mui.Paper>
    </Mui.Box>
  );
};
export default VehicleEntranceInternalDDT;
